/**
 * <mj-header> -- the drawer behind sections/mj-header.liquid.
 *
 * The header itself is CSS; the only thing that needs script is the mobile drawer,
 * which has to trap focus, restore it on close, and keep the toggle's aria-expanded
 * honest. Everything else (the nav, the dropdown, the icons) works without JS, so if
 * this file fails to load the header still navigates -- only the drawer stops opening,
 * and the links inside it are reachable from the footer and the site map.
 */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

class MjHeader extends HTMLElement {
  connectedCallback() {
    this.#publishHeight();

    this.drawer = this.querySelector('[data-mj-drawer]');
    this.toggle = this.querySelector('[data-mj-drawer-open]');
    if (!this.drawer || !this.toggle) return;

    this.controller = new AbortController();
    const { signal } = this.controller;

    // The toggle is also the close control: the comp keeps the header on screen while
    // the drawer is open and turns this button into the X.
    this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()), {
      signal,
    });

    this.querySelectorAll('[data-mj-drawer-close]').forEach((node) => {
      node.addEventListener('click', () => this.close(), { signal });
    });

    /*
     * The drawer's second layer: one push panel per nav link that has mega-menu blocks.
     * Both layers are already in the markup -- all this does is swap which one is on
     * screen, so with no script the root list is what shows and every link still works.
     */
    this.drawerRoot = this.querySelector('[data-mj-drawer-root]');
    this.subpanels = Array.from(this.querySelectorAll('[data-mj-subpanel]'));

    this.querySelectorAll('[data-mj-subpanel-open]').forEach((node) => {
      node.addEventListener(
        'click',
        () => this.openSubpanel(node.dataset.mjSubpanelOpen, node),
        { signal }
      );
    });

    this.querySelectorAll('[data-mj-subpanel-back]').forEach((node) => {
      /*
       * `detail` is how many times the pointer was clicked, and a button activated from
       * the keyboard reports 0 -- so this is the one reliable way to tell Enter/Space
       * from a tap. It decides where focus goes next; see closeSubpanel.
       */
      node.addEventListener('click', (event) => this.closeSubpanel(event.detail === 0), {
        signal,
      });
    });

    // A link inside the drawer navigates; on a same-page anchor it would otherwise
    // leave the drawer sitting open over the destination.
    this.drawer.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('a[href]')) this.close();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') this.close();
        else if (event.key === 'Tab') this.#trapFocus(event);
      },
      { signal }
    );

    /*
     * Resizing past the breakpoint hides the drawer in CSS. Without this the page would
     * stay scroll-locked with no visible way to release it.
     */
    // The drawer hangs off the header's bottom edge, so anything that changes the
    // header's height has to move it. Only while open -- there is nothing to place
    // otherwise.
    window.addEventListener(
      'resize',
      () => {
        if (this.isOpen) this.#positionDrawer();
      },
      { signal }
    );

    this.desktop = window.matchMedia('(min-width: 48em)');
    this.desktop.addEventListener('change', (event) => {
      if (event.matches && this.isOpen) this.close();
    }, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.heightObserver?.disconnect();
    this.#unlockScroll();
  }

  /**
   * Keeps `--header-height` honest.
   *
   * Horizon sets that variable by measuring `#header-component`, which belonged to the
   * stock header this section replaced. The 60px fallback in `sections/header.liquid`
   * still reaches the page -- section stylesheets are bundled theme-wide whether or not
   * the section renders -- so nothing breaks without this, it just reports a height no
   * element actually has. Product pages read it for their sticky offset.
   *
   * `--header-group-height` needs no equivalent: header.js measures the group's children
   * directly, and this section is one of them.
   */
  #publishHeight() {
    const publish = () => {
      const height = Math.round(this.getBoundingClientRect().height);
      if (height > 0) document.body.style.setProperty('--header-height', `${height}px`);
    };

    publish();
    this.heightObserver = new ResizeObserver(publish);
    this.heightObserver.observe(this);
  }

  get isOpen() {
    return !this.drawer.hasAttribute('hidden');
  }

  /**
   * Publishes the header's bottom edge as `--mj-drawer-top`.
   *
   * The drawer starts below the header instead of covering it (4480:17655), and the
   * header's height is not a constant -- the announcement bar can be switched off and
   * the logo height is a setting. Measuring is the only honest answer. The CSS falls
   * back to 0, which covers the header rather than breaking the drawer.
   */
  #positionDrawer() {
    const bottom = Math.max(0, Math.round(this.getBoundingClientRect().bottom));
    this.drawer.style.setProperty('--mj-drawer-top', `${bottom}px`);
  }

  open() {
    this.returnFocusTo = document.activeElement;
    this.drawer.removeAttribute('hidden');
    this.#positionDrawer();
    this.toggle.setAttribute('aria-expanded', 'true');
    this.#lockScroll();

    // The dialog itself, not the first row in it -- see the markup for why.
    this.drawer.querySelector('[role="dialog"]')?.focus({ preventScroll: true });
  }

  openSubpanel(handle, opener) {
    const panel = this.subpanels.find((node) => node.dataset.mjSubpanel === handle);
    if (!panel) return;

    this.subpanelOpener = opener;
    this.drawerRoot?.setAttribute('hidden', '');
    panel.removeAttribute('hidden');

    // Focus lands on the panel, so a screen reader reads it from the top and no ring is
    // drawn on a row nobody chose. The back button is the first thing inside it, so it is
    // still the first stop on tab -- the way out stays the way out.
    panel.focus({ preventScroll: true });
  }

  closeSubpanel(viaKeyboard = false) {
    for (const panel of this.subpanels) panel.setAttribute('hidden', '');
    this.drawerRoot?.removeAttribute('hidden');

    /*
     * Where focus goes depends on how Back was activated, because the two users want
     * opposite things.
     *
     * From the keyboard: back to the row that opened the subpanel, so tabbing carries on
     * from where it left off instead of restarting at the top -- and the ring that comes
     * with it is the point, since that is how a keyboard user sees where they are.
     *
     * From a tap: onto the list itself. Focusing the row would leave a ring sitting on
     * Engagement Rings, which reads as a selection to someone who never asked for one.
     * `:focus-visible` is meant to answer this, but Safari treats a programmatic focus as
     * keyboard focus and draws the ring anyway -- so the choice is made here instead of
     * left to the browser. Focus still lands inside the drawer, so the trap holds.
     */
    if (viaKeyboard) {
      this.subpanelOpener?.focus?.();
    } else {
      this.drawerRoot?.focus?.({ preventScroll: true });
    }

    this.subpanelOpener = null;
  }

  close() {
    if (!this.isOpen) return;

    /*
     * Reset to the root list on the way out. Reopening the drawer to the middle of a
     * category nobody asked for reads as a bug, and the back button is off screen.
     */
    this.subpanelOpener = null;
    this.closeSubpanel();

    this.drawer.setAttribute('hidden', '');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.#unlockScroll();

    // Focus goes back where it came from, not to the top of the document.
    this.returnFocusTo?.focus?.();
  }

  #trapFocus(event) {
    const items = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
      (node) => node.offsetParent !== null
    );
    if (items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;

    /*
     * The second test covers the panel itself. Focus starts there on open, and a panel is
     * `tabindex="-1"`, so it is not in `items` and matches neither end -- without this,
     * the very first shift+Tab after opening would step straight back out of the drawer.
     */
    if (event.shiftKey && (active === first || (this.drawer.contains(active) && !items.includes(active)))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #lockScroll() {
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  #unlockScroll() {
    if (this.previousOverflow === undefined) return;
    document.body.style.overflow = this.previousOverflow;
    this.previousOverflow = undefined;
  }
}

if (!customElements.get('mj-header')) {
  customElements.define('mj-header', MjHeader);
}
